import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { useTerm } from "@/lib/terminology.jsx";
import { ListTodo, Plus } from "lucide-react";
import TaskList from "@/components/TaskList";
import TaskModal from "@/components/TaskModal";
import TaskDetailPanel from "@/components/TaskDetailPanel";
import DealChoicePopover from "@/components/DealChoicePopover";

const BATCH_SIZE = 25; // CW6.2.7

export default function Tasks() {
  const { user } = useAuth();
  const term = useTerm();
  // CW6.2.7: paginated open + completed, separate counts
  const [openTasks, setOpenTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [counts, setCounts] = useState({ open: 0, completed: 0 });
  const [openOffset, setOpenOffset] = useState(0);
  const [openHasMore, setOpenHasMore] = useState(true);
  const [openLoading, setOpenLoading] = useState(false);
  const [openError, setOpenError] = useState(null);
  const [completedLoaded, setCompletedLoaded] = useState(false);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [completedOffset, setCompletedOffset] = useState(0);
  const [completedHasMore, setCompletedHasMore] = useState(true);
  const [completedError, setCompletedError] = useState(null);
  const [scope, setScope] = useState("mine");
  const [showNew, setShowNew] = useState(false);
  const [openTaskId, setOpenTaskId] = useState(null);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  // Snooze flow state
  const [snoozeTask, setSnoozeTask] = useState(null);
  const [snoozeUntil, setSnoozeUntil] = useState("");

  // Complete flow state
  const [completeTask, setCompleteTask] = useState(null);
  const [createFollowup, setCreateFollowup] = useState(false);

  // Follow-up state
  const [followupPrefill, setFollowupPrefill] = useState(null);
  const [followupOriginalId, setFollowupOriginalId] = useState(null);

  // ----- Load counts -----
  const loadCounts = useCallback(async () => {
    const params = {};
    if (scope === "mine") params.assigned_to_me = true;
    try {
      const { data } = await api.get("/v513/tasks/counts", { params });
      setCounts({ open: data.open || 0, completed: data.completed || 0 });
    } catch (_e) { /* non-fatal */ }
  }, [scope]);

  // ----- Load Open batch -----
  const loadOpenBatch = useCallback(async (offset = 0, append = false) => {
    const params = { status: "open", limit: BATCH_SIZE, offset };
    if (scope === "mine") params.assigned_to_me = true;
    setOpenLoading(true);
    setOpenError(null);
    try {
      const { data } = await api.get("/v513/tasks", { params });
      setOpenTasks((prev) => (append ? [...prev, ...data] : data));
      setOpenOffset(offset + data.length);
      setOpenHasMore(data.length === BATCH_SIZE);
    } catch (err) {
      setOpenError(err?.response?.data?.detail || "Failed to load tasks");
    } finally {
      setOpenLoading(false);
    }
  }, [scope]);

  // ----- Load Completed batch (lazy) -----
  const loadCompletedBatch = useCallback(async (offset = 0, append = false) => {
    const params = { status: "completed", limit: BATCH_SIZE, offset };
    if (scope === "mine") params.assigned_to_me = true;
    setCompletedLoading(true);
    setCompletedError(null);
    try {
      const { data } = await api.get("/v513/tasks", { params });
      setCompletedTasks((prev) => (append ? [...prev, ...data] : data));
      setCompletedOffset(offset + data.length);
      setCompletedHasMore(data.length === BATCH_SIZE);
      setCompletedLoaded(true);
    } catch (err) {
      setCompletedError(err?.response?.data?.detail || "Failed to load");
    } finally {
      setCompletedLoading(false);
    }
  }, [scope]);

  // Reset on scope change
  useEffect(() => {
    setOpenTasks([]);
    setOpenOffset(0);
    setOpenHasMore(true);
    setCompletedTasks([]);
    setCompletedOffset(0);
    setCompletedHasMore(true);
    setCompletedLoaded(false);
    loadOpenBatch(0, false);
    loadCounts();
  }, [scope, loadOpenBatch, loadCounts]);

  // Refresh after CRUD actions without losing scroll (reset + refetch pages
  // we've already seen).
  const refresh = useCallback(async () => {
    const seen = Math.max(BATCH_SIZE, openOffset);
    const params = { status: "open", limit: seen, offset: 0 };
    if (scope === "mine") params.assigned_to_me = true;
    try {
      const { data } = await api.get("/v513/tasks", { params });
      setOpenTasks(data);
      setOpenOffset(data.length);
      setOpenHasMore(data.length === seen);
    } catch (_e) { /* fallback to fresh load */ loadOpenBatch(0, false); }
    if (completedLoaded) {
      const cparams = { status: "completed", limit: Math.max(BATCH_SIZE, completedOffset), offset: 0 };
      if (scope === "mine") cparams.assigned_to_me = true;
      try {
        const { data } = await api.get("/v513/tasks", { params: cparams });
        setCompletedTasks(data);
        setCompletedOffset(data.length);
      } catch (_e) { /* ignore */ }
    }
    loadCounts();
  }, [openOffset, completedOffset, completedLoaded, scope, loadCounts, loadOpenBatch]);

  const load = refresh; // alias for existing callers below

  // Infinite scroll — observe a sentinel at the bottom of the Open list
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (!openHasMore || openLoading) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadOpenBatch(openOffset, true);
      }
    }, { rootMargin: "200px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [openHasMore, openLoading, openOffset, loadOpenBatch]);

  const openLinksOf = (t) =>
    (t.linked_deals || []).filter((d) => d.status !== "completed");

  // ---- Snooze ----
  const submitSnooze = async (dealIds) => {
    if (!snoozeTask) return;
    if (!snoozeUntil) {
      toast.error("Choose a date");
      return;
    }
    try {
      const payload = { snooze_until: snoozeUntil };
      const openLinks = openLinksOf(snoozeTask);
      if (openLinks.length >= 1) {
        payload.deal_ids = dealIds;
      }
      await api.post(`/v513/tasks/${snoozeTask.task_id}/snooze`, payload);
      const names =
        (dealIds || []).length && snoozeTask.linked_deals
          ? snoozeTask.linked_deals
              .filter((d) => dealIds.includes(d.deal_id))
              .map((d) => d.company_name)
              .join(", ")
          : "";
      toast.success(
        names
          ? `Snoozed for ${names}`
          : `${term("Task")} snoozed`
      );
      setSnoozeTask(null);
      setSnoozeUntil("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  // ---- Complete ----
  const submitComplete = async (dealIds) => {
    if (!completeTask) return;
    try {
      const openLinks = openLinksOf(completeTask);
      const payload = { create_followup: createFollowup };
      if (openLinks.length >= 1) payload.deal_ids = dealIds;
      const { data } = await api.post(
        `/v513/tasks/${completeTask.task_id}/complete`,
        payload
      );
      const names =
        (dealIds || []).length && completeTask.linked_deals
          ? completeTask.linked_deals
              .filter((d) => dealIds.includes(d.deal_id))
              .map((d) => d.company_name)
              .join(", ")
          : "";
      toast.success(
        names
          ? `Completed for ${names}`
          : `${term("Task")} completed`
      );
      const origId = completeTask.task_id;
      setCompleteTask(null);
      if (createFollowup && data?.follow_up_prefill) {
        setFollowupPrefill(data.follow_up_prefill);
        setFollowupOriginalId(origId);
      } else {
        load();
      }
      setCreateFollowup(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="cw-caption uppercase tracking-widest">Work</div>
          <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
            <ListTodo size={22} /> {term("Tasks")}
          </h1>
          <p className="text-sm text-[var(--cw-muted)] mt-1">
            Every {term("task").toLowerCase()} in your workspace. Click any row to open
            its full history and edit any field.
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="cw-btn-primary text-sm inline-flex items-center gap-1"
          data-testid="new-task-btn"
        >
          <Plus size={14} /> New {term("Task")}
        </button>
      </div>

      <div className="mt-6 flex gap-3">
        <div
          className="inline-flex rounded-md border overflow-hidden"
          style={{ borderColor: "var(--cw-border)" }}
        >
          {[
            { k: "mine", l: "Assigned to me" },
            { k: "all", l: `All tenant ${term("tasks").toLowerCase()}` },
          ].map((b) => (
            <button
              key={b.k}
              onClick={() => setScope(b.k)}
              className={`text-xs px-3 py-1.5 ${
                scope === b.k ? "font-semibold" : "text-[var(--cw-muted)]"
              }`}
              style={
                scope === b.k
                  ? { background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }
                  : {}
              }
              data-testid={`tasks-scope-${b.k}`}
            >
              {b.l}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4" ref={scrollRef}>
        <TaskList
          openTasks={openTasks}
          completedTasks={completedTasks}
          openCount={counts.open}
          completedCount={counts.completed}
          openHasMore={openHasMore}
          openLoading={openLoading}
          openError={openError}
          onRetryOpen={() => loadOpenBatch(openOffset, openOffset > 0)}
          sentinelRef={sentinelRef}
          completedLoaded={completedLoaded}
          completedLoading={completedLoading}
          completedHasMore={completedHasMore}
          completedError={completedError}
          onExpandCompleted={() => { if (!completedLoaded) loadCompletedBatch(0, false); }}
          onLoadMoreCompleted={() => loadCompletedBatch(completedOffset, true)}
          onOpen={(t) => setOpenTaskId(t.task_id)}
          onCompleteClick={(t) => setCompleteTask(t)}
          onSnoozeClick={(t) => setSnoozeTask(t)}
          showLinkedDeals
        />
      </div>

      {showNew && (
        <TaskModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            load();
          }}
        />
      )}

      {openTaskId && (
        <TaskDetailPanel
          taskId={openTaskId}
          onClose={() => setOpenTaskId(null)}
          onChanged={load}
        />
      )}

      {/* Snooze — always with per-deal picker when task has linked deals */}
      {snoozeTask && (
        <SnoozeFlow
          task={snoozeTask}
          snoozeUntil={snoozeUntil}
          setSnoozeUntil={setSnoozeUntil}
          onCancel={() => {
            setSnoozeTask(null);
            setSnoozeUntil("");
          }}
          onConfirm={submitSnooze}
        />
      )}

      {/* Complete — always with per-deal picker when task has linked deals */}
      {completeTask && (
        <CompleteFlow
          task={completeTask}
          createFollowup={createFollowup}
          setCreateFollowup={setCreateFollowup}
          onCancel={() => {
            setCompleteTask(null);
            setCreateFollowup(false);
          }}
          onConfirm={submitComplete}
        />
      )}

      {followupPrefill && (
        <TaskModal
          prefill={followupPrefill}
          onClose={() => {
            setFollowupPrefill(null);
            setFollowupOriginalId(null);
            load();
          }}
          onCreated={async (newId) => {
            if (followupOriginalId) {
              try {
                await api.post(`/v513/tasks/${followupOriginalId}/log-followup`, {
                  new_task_id: newId,
                });
              } catch {
                /* non-fatal */
              }
            }
            setFollowupPrefill(null);
            setFollowupOriginalId(null);
            load();
          }}
          title={`Follow-up ${term("Task")}`}
        />
      )}
    </div>
  );
}

/** Snooze — decides whether to show a picker based on link count */
function SnoozeFlow({ task, snoozeUntil, setSnoozeUntil, onCancel, onConfirm }) {
  const term = useTerm();
  const openLinks = (task.linked_deals || []).filter(
    (d) => d.status !== "completed"
  );

  const dateInput = (
    <div className="mt-3">
      <div className="cw-caption uppercase text-xs">Snooze until</div>
      <input
        type="date"
        value={snoozeUntil}
        onChange={(e) => setSnoozeUntil(e.target.value)}
        className="w-full h-9 px-3 rounded-md border text-sm mt-1"
        style={{ borderColor: "var(--cw-border)" }}
        data-testid="snooze-date-input"
      />
    </div>
  );

  // 0 or 1 open link → skip picker
  if (openLinks.length <= 1) {
    return (
      <div
        className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
        onClick={onCancel}
        data-testid="snooze-popover"
      >
        <div
          className="bg-white rounded-xl w-full max-w-sm p-5"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="cw-h2 text-base">Snooze '{task.task_name}'</h3>
          {openLinks.length === 1 && (
            <div className="text-xs text-[var(--cw-muted)] mt-1">
              Snoozing only for {openLinks[0].company_name} — every other linked{" "}
              {term("deal").toLowerCase()} is unaffected.
            </div>
          )}
          {dateInput}
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={onCancel} className="cw-btn-ghost text-sm">
              Cancel
            </button>
            <button
              disabled={!snoozeUntil}
              onClick={() =>
                onConfirm(openLinks.length ? [openLinks[0].deal_id] : [])
              }
              className={`cw-btn-primary text-sm ${!snoozeUntil ? "opacity-40" : ""}`}
              data-testid="snooze-confirm"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ≥2 open links → picker
  return (
    <DealChoicePopover
      open
      onClose={onCancel}
      onConfirm={onConfirm}
      title={`Snooze '${task.task_name}'`}
      description={`Choose which linked ${term("deals").toLowerCase()} to snooze. Only those ${term("deals").toLowerCase()} will have their due date pushed — every other linked ${term("deal").toLowerCase()} stays exactly as it is.`}
      actionLabel="Snooze"
      openLinkedDeals={openLinks}
      extra={dateInput}
      testid="snooze-choice"
    />
  );
}

/** Complete — decides whether to show a picker based on link count */
function CompleteFlow({
  task,
  createFollowup,
  setCreateFollowup,
  onCancel,
  onConfirm,
}) {
  const term = useTerm();
  const openLinks = (task.linked_deals || []).filter(
    (d) => d.status !== "completed"
  );

  const followupToggle = (
    <label className="mt-3 flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={createFollowup}
        onChange={(e) => setCreateFollowup(e.target.checked)}
        data-testid="complete-followup-checkbox"
      />
      Create a follow-up {term("task").toLowerCase()}
    </label>
  );

  // 0 or 1 open link → simple confirm
  if (openLinks.length <= 1) {
    return (
      <div
        className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
        onClick={onCancel}
        data-testid="complete-popover"
      >
        <div
          className="bg-white rounded-xl w-full max-w-sm p-5"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="cw-h2 text-base">
            Mark '{task.task_name}' as complete?
          </h3>
          {openLinks.length === 1 && (
            <div className="text-xs text-[var(--cw-muted)] mt-1">
              Completing only for {openLinks[0].company_name} — every other linked{" "}
              {term("deal").toLowerCase()} is unaffected.
            </div>
          )}
          {followupToggle}
          <div className="text-xs text-[var(--cw-muted)] mt-2">
            Completing is permanent for the chosen {term("deal").toLowerCase()} — it
            won't reopen.
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={onCancel} className="cw-btn-ghost text-sm">
              Cancel
            </button>
            <button
              onClick={() =>
                onConfirm(openLinks.length ? [openLinks[0].deal_id] : [])
              }
              className="cw-btn-primary text-sm"
              data-testid="complete-confirm"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ≥2 open links → picker
  return (
    <DealChoicePopover
      open
      onClose={onCancel}
      onConfirm={onConfirm}
      title={`Mark '${task.task_name}' as complete`}
      description={`Completing is permanent for the chosen ${term("deals").toLowerCase()} — they will not reopen. Every other linked ${term("deal").toLowerCase()} stays open.`}
      actionLabel="Complete"
      openLinkedDeals={openLinks}
      extra={followupToggle}
      testid="complete-choice"
    />
  );
}
